import {
  Box,
  Button,
  CircularProgress,
  CircularProgressLabel,
  Flex,
  Grid,
  Text,
  Image,
} from '@chakra-ui/react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ReactSVG } from 'react-svg';
import QRCode from 'qrcode.react';
import moment from 'moment';
import { throttle } from 'lodash';
import logo from 'assets/img/logo.png';
import { applySeatStyling, colorBackGround } from 'helpers/seatStyling';
import { useCustomerDisplayState } from 'helpers/customerDisplayChannel';
import {
  showTextFloor,
  BOOKING_STATUS_CANCEL,
  BOOKING_STATUS_SUCCESS,
  PAYMENT_TYPE_PAID,
} from 'variables/columnsData';
import EventCalendar from 'components/Calendars/EventCalendar';
import './project.css';

// Dark backdrop for the confirmation ticket card; exported so
// CustomerDisplayPreview.js can match it on its own scroll container.
export const TICKET_PAGE_BG = '#0f1512';

// Status 2 is Cancel, status 1 + payment_type 1 is a fully paid, successful
// booking. Anything else (still unpaid/liability, or a booking still
// mid-flow with no status yet) has no outcome to show.
function getBookingOutcome(data) {
  if (!data) return null;
  if (data.status === BOOKING_STATUS_CANCEL) return 'cancelled';
  if (data.status === BOOKING_STATUS_SUCCESS && data.paymentType === PAYMENT_TYPE_PAID) {
    return 'success';
  }
  return null;
}

// How long the outcome ribbon stays up before this screen returns to
// Welcome on its own — the booking is fully resolved either way, so there's
// nothing left for the operator to do here.
const OUTCOME_AUTO_IDLE_MS = 10000;

// Counter-facing screen, opened as a separate tab/window, driven entirely by
// BroadcastChannel messages. Full-viewport so it covers the admin layout.
export default function BookingCustomerDisplay() {
  const scrollElRef = useRef(null);

  // Applies the scroll position the operator picked in CustomerDisplayPreview.js
  // (their own screen's mirrored, scrollable mini-copy of this display) — this
  // screen is never scrolled directly by anyone at the counter, only remotely.
  const handleScrollSync = useCallback((ratio) => {
    const el = scrollElRef.current;
    if (!el) return;
    const max = el.scrollHeight - el.clientHeight;
    el.scrollTop = max > 0 ? ratio * max : 0;
  }, []);

  // Replies to CustomerDisplayPreview.js asking (on its own mount) to catch
  // up on our current size — the current screen itself is answered
  // separately, straight from the hook's own state (see 'request-sync' in
  // helpers/customerDisplayChannel.js).
  const handleSyncRequested = useCallback((post) => {
    post({ type: 'viewport-size', width: window.innerWidth, height: window.innerHeight });
  }, []);

  const { screen, sendMessage, goIdle, isCurtainClosed } = useCustomerDisplayState({
    onScrollSync: handleScrollSync,
    onSyncRequested: handleSyncRequested,
  });

  // Once a booking finishes (paid in full, or cancelled) this screen shows
  // an outcome ribbon on the ticket (see OutcomeRibbon/ConfirmationScreen
  // below) then returns to Welcome on its own after a short delay, ready
  // for the next guest — nobody needs to dismiss it manually at the counter.
  const bookingOutcome =
    screen.type === 'confirmation' ? getBookingOutcome(screen.data) : null;
  const bookingCode = screen.data?.bookingCode;
  useEffect(() => {
    if (!bookingOutcome) return;
    const timer = setTimeout(() => goIdle(bookingCode), OUTCOME_AUTO_IDLE_MS);
    return () => clearTimeout(timer);
  }, [bookingOutcome, bookingCode, goIdle]);

  // Reports real viewport size so the preview renders its mirror at the same
  // width (see 'viewport-size' in helpers/customerDisplayChannel.js).
  useEffect(() => {
    const reportSize = () => {
      sendMessage({ type: 'viewport-size', width: window.innerWidth, height: window.innerHeight });
    };
    reportSize();
    const throttledReportSize = throttle(reportSize, 300);
    window.addEventListener('resize', throttledReportSize);
    return () => {
      window.removeEventListener('resize', throttledReportSize);
      throttledReportSize.cancel();
    };
  }, [sendMessage]);

  // Tells CustomerDisplayPreview.js to hide itself the moment this window/tab
  // closes, instead of continuing to show a now-stale mirror on the
  // operator's screen with nothing real behind it anymore.
  useEffect(() => {
    const handleBeforeUnload = () => {
      sendMessage({ type: 'display-offline' });
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [sendMessage]);

  return (
    <>
      <Box
        ref={scrollElRef}
        position='fixed'
        top={0}
        left={0}
        w='100vw'
        h='100vh'
        zIndex={9999}
        bg={!isCurtainClosed && screen.type === 'confirmation' ? TICKET_PAGE_BG : 'white'}
        overflow='auto'
      >
        {/* Curtain: forces Welcome without touching the booking flow
            underneath; the preview keeps mirroring the real `screen`
            regardless. */}
        {isCurtainClosed ? <IdleScreen /> : <CustomerDisplayScreenContent screen={screen} />}
      </Box>
      {/* Sibling of the scrollable Box, not nested in it — a fixed overlay
          inside a scroll container would scroll away with its content. */}
      {!isCurtainClosed && <QrPaymentOverlay qrPayment={screen.data?.qrPayment} />}
    </>
  );
}

// Screens with their own inline countdown in the sticky header — everything
// else falls back to the standalone SeatHoldCountdown below.
const SCREEN_COMPONENTS = {
  confirmation: ConfirmationScreen,
  seating: SeatingScreen,
  calendar: CalendarScreen,
};

// Inner content only (no fixed full-viewport wrapper) so it can be reused,
// scaled down, by CustomerDisplayPreview.js on the operator's own screen.
// `isPreview` disables position:sticky on the header there — see StickyHeader.
export function CustomerDisplayScreenContent({ screen, isPreview = false }) {
  const ScreenComponent = SCREEN_COMPONENTS[screen.type];
  return (
    <>
      {ScreenComponent ? <ScreenComponent data={screen.data} isPreview={isPreview} /> : <IdleScreen />}
      {!ScreenComponent && <SeatHoldCountdown expireAt={screen.data?.expireAt} />}
    </>
  );
}

// Shared tick logic behind both countdown renderings. Deliberately does not
// reuse components/CountDown/CountDown.js, which also cancels the booking
// and navigates away on expiry — that side effect must stay owned by the
// operator tab only, this screen just displays the same countdown.
function useTimeLeft(expireAt) {
  const [timeLeft, setTimeLeft] = useState(null);

  useEffect(() => {
    if (!expireAt) {
      setTimeLeft(null);
      return;
    }
    const tick = () => {
      const duration = moment.duration(moment(expireAt).diff(moment()));
      setTimeLeft({
        minutes: Math.max(0, duration.minutes()),
        seconds: Math.max(0, duration.seconds()),
      });
    };
    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [expireAt]);

  return timeLeft;
}

function SeatHoldCountdown({ expireAt }) {
  const timeLeft = useTimeLeft(expireAt);
  if (!timeLeft) return null;

  return (
    <>
      <TimeBadge value={timeLeft.minutes} max={14} label='Minutes' right={{ base: '70px', lg: '105px' }} />
      <TimeBadge value={timeLeft.seconds} max={60} label='Seconds' right={{ base: '24px', lg: '35px' }} />
    </>
  );
}

// Ring itself, shared by the floating badge (Confirmation/Idle screens) and
// the inline pair embedded in the sticky header (Seating screen). Mirrors
// components/CountDown/CountDown.js's ring styling exactly.
function CountdownRing({
  value,
  max,
  label,
  size,
  valueFontSize = '23px',
  labelFontSize = '10px',
  bold = false,
  showLabel = true,
  labelColor,
}) {
  return (
    <CircularProgress
      value={value}
      max={max}
      size={size || { base: '46px', lg: '70px' }}
      thickness='4px'
      color='green'
      sx={{
        '@media (max-width: 767px)': {
          fontSize: '46px',
          '& .label-countdown': {
            display: 'none',
          },
        },
      }}
    >
      <CircularProgressLabel fontSize='sm'>
        <Flex direction='column'>
          <Text fontSize={valueFontSize} fontWeight={bold ? 'bold' : 'normal'} color='green.400'>
            {value}
          </Text>
          {showLabel && (
            <Text
              className='label-countdown'
              fontSize={labelFontSize}
              fontWeight={bold ? 'bold' : 'normal'}
              color={labelColor}
            >
              {label}
            </Text>
          )}
        </Flex>
      </CircularProgressLabel>
    </CircularProgress>
  );
}

function TimeBadge({ value, max, label, right }) {
  return (
    <Button
      h={{ base: '40px', lg: '60px' }}
      w={{ base: '40px', lg: '60px' }}
      bg='white'
      position='fixed'
      right={right}
      bottom='30px'
      borderRadius='50px'
      boxShadow='0 2px 12px 0 rgb(0 0 0 / 16%)'
      display='flex'
    >
      <CountdownRing value={value} max={max} label={label} />
    </Button>
  );
}

// Same dual-ring countdown as the operator screen, just laid out inline in
// the sticky header instead of floating fixed at the bottom-right.
function HeaderCountdown({ expireAt, dark = false }) {
  const timeLeft = useTimeLeft(expireAt);
  if (!timeLeft) return null;

  const labelColor = dark ? 'whiteAlpha.700' : undefined;

  return (
    <Flex align='center' gap={3} ml='auto' pl={4}>
      <CountdownRing
        value={timeLeft.minutes}
        max={14}
        label='Minutes'
        size='54px'
        valueFontSize='17px'
        labelFontSize='7px'
        labelColor={labelColor}
        bold
      />
      <CountdownRing
        value={timeLeft.seconds}
        max={60}
        label='Seconds'
        size='54px'
        valueFontSize='17px'
        labelFontSize='7px'
        labelColor={labelColor}
        bold
      />
    </Flex>
  );
}

function IdleScreen() {
  return (
    <Flex direction='column' align='center' justify='center' h='100%' gap={6}>
      <Image src={logo} alt='Lune' maxW='260px' maxH='180px' objectFit='contain' />
      <Text fontSize='5xl' fontWeight='bold' color='gray.700'>
        Welcome!
      </Text>
      <Text fontSize='xl' color='gray.500'>
        We're happy to have you here
      </Text>
    </Flex>
  );
}

// Same chip styling as ZoneBadge, fixed brand color instead of the zone's
// (booking code isn't tied to a zone) — shown to its left so the guest can
// see the code without it clashing with the one in the header above.
function BookingCodeBadge({ data }) {
  if (!data.bookingCode) return null;
  return (
    <Box
      border='1.5px solid'
      borderColor='gray.300'
      bg='gray.100'
      borderRadius='15px'
      px='17px'
      py='11px'
      textAlign='center'
      flexShrink={0}
    >
      <Text fontSize='10px' fontWeight='700' letterSpacing='0.14em' color='gray.800'>BOOKING CODE</Text>
      <Text fontSize='16px' fontWeight='800' color='gray.800' whiteSpace='nowrap'>{data.bookingCode}</Text>
    </Box>
  );
}

// Bordered chip, tinted with the booking's own zone color, shown next to
// the show title instead of buried in the Date/Time/Adult/Child grid.
function ZoneBadge({ data }) {
  if (!data.zone) return null;
  const zoneId = data.seats?.[0]?.zone?.id ?? data.seats?.[0]?.zone_id;
  const color = colorBackGround(zoneId) || '#4A5568';
  return (
    <Box
      border='1.5px solid'
      borderColor={color}
      bg={`${color}1A`}
      borderRadius='15px'
      px='17px'
      py='11px'
      textAlign='center'
      flexShrink={0}
    >
      <Text fontSize='10px' fontWeight='700' letterSpacing='0.14em' color={color}>ZONE</Text>
      <Text fontSize='19px' fontWeight='800' color={color}>{data.zone}</Text>
    </Box>
  );
}

function InfoItem({ label, value }) {
  return (
    <Box minW={0}>
      <Text fontSize='10px' fontWeight='700' letterSpacing='0.16em' textTransform='uppercase' color='gray.500'>
        {label}
      </Text>
      <Text fontSize='19px' fontWeight='700' letterSpacing='-0.01em' whiteSpace='nowrap' color='gray.800'>
        {value === undefined || value === null || value === '' ? '—' : value}
      </Text>
    </Box>
  );
}

// Collapses per-seat rows to one line per distinct amount (grouped by price,
// not program, since only the resulting price is shown here).
function groupPaymentRows(rows) {
  const groups = [];
  const byAmount = new Map();
  (rows || []).forEach((row) => {
    let group = byAmount.get(row.amount);
    if (!group) {
      group = {
        key: row.amount,
        unitAmount: row.amount,
        seatNumbers: [],
      };
      byAmount.set(row.amount, group);
      groups.push(group);
    }
    group.seatNumbers.push(row.seatNumber);
  });
  return groups;
}

// Booker/Audience mini-cards — only rendered once BookingDetail.js has
// broadcast real party info, so mid-flow (still on Payment) they stay hidden
// instead of showing empty cards.
function PartyCard({ label, party }) {
  if (!party?.name) return null;
  return (
    <Box bg='gray.50' border='1px solid #E2E8F0' borderRadius='16px' px='18px' py='16px'>
      <Text fontSize='10px' fontWeight='700' letterSpacing='0.16em' color='gray.500' mb={1}>{label}</Text>
      <Text fontWeight='800' fontSize='18px' letterSpacing='-0.01em' color='gray.800'>{party.name}</Text>
      {party.email && (
        <Text fontSize='14px' lineHeight='1.45' color='gray.500'>{party.email}</Text>
      )}
      {party.phone && (
        <Text fontSize='14px' lineHeight='1.45' color='gray.500'>{party.phone}</Text>
      )}
    </Box>
  );
}

// Total/Paid/Remaining, sourced from BookingDetail.js's Summary block — only
// meaningful once the booking actually exists there, so absent mid-flow.
function BalanceCard({ balance }) {
  if (!balance) return null;
  const total = balance.total || 0;
  const showPaid = balance.paid != null;
  const paid = showPaid ? balance.paid : 0;
  // Omitted (not 0) for Official-Price channel groups, whose paid/remaining
  // track the channel's own agreed Amount, not the Official Price shown here.
  const showRemaining = balance.remaining != null;
  const remaining = showRemaining ? balance.remaining : 0;
  const ratio = total > 0 ? Math.min(1, Math.max(0, paid / total)) : 0;
  const statusColor =
    balance.status === 'FULLY PAID' ? 'green' : balance.status === 'UNPAID' ? 'red' : 'orange';

  return (
    <Box bg='white' border='1px solid #E2E8F0' borderRadius='16px' px='18px' py='16px' mt={4}>
      <Flex justify='space-between' align='center' mb={2}>
        <Text fontSize='10px' fontWeight='700' letterSpacing='0.16em' color='gray.500'>BALANCE</Text>
        {balance.status && (
          <Box
            bg={`${statusColor}.50`}
            color={`${statusColor}.600`}
            borderRadius='full'
            px={3}
            py={1}
            fontSize='13px'
            fontWeight='800'
            letterSpacing='0.08em'
          >
            {balance.status === 'FULLY PAID' ? 'PAID' : balance.status}
          </Box>
        )}
      </Flex>
      <Flex justify='space-between' align='center' mb={0.5}>
        <Text fontSize='14px' color='gray.500'>Total</Text>
        <Text fontWeight='700' fontSize='18px'>{total.toLocaleString('en-US')}</Text>
      </Flex>
      {showPaid && (
        <Flex justify='space-between' align='center' mb={1.5}>
          <Text fontSize='14px' color='gray.500'>Paid</Text>
          <Text fontWeight='700' fontSize='18px'>{paid.toLocaleString('en-US')}</Text>
        </Flex>
      )}
      {showRemaining && (
        <>
          <Box bg='gray.100' borderRadius='full' h='8px' overflow='hidden' mb={2.5}>
            <Box bg='green.400' h='100%' w={`${ratio * 100}%`} />
          </Box>
          <Text fontSize='10px' fontWeight='700' letterSpacing='0.16em' color='gray.500' mb={0.5}>REMAINING</Text>
          <Text fontSize='34px' fontWeight='800' letterSpacing='-0.02em' lineHeight='1' color={remaining > 0 ? 'red.500' : 'green.500'}>
            {remaining.toLocaleString('en-US')}{' '}
            <Text as='span' fontSize='14px' fontWeight='700'>VND</Text>
          </Text>
        </>
      )}
    </Box>
  );
}

// Mirrors BookingDetail.js's "Scan to Pay" modal so the guest can see it —
// the operator faces away from the counter. A blocking overlay (not inline)
// since the ticket card can scroll out of view. `qrPayment` is null once
// resolved/closed (see BookingDetail.js).
//
// `position`/`scale` differ by caller: the real display has nothing else
// establishing a containing block, so 'fixed' pins to the viewport there.
// CustomerDisplayPreview.js renders this as a sibling of its transform-scaled
// mirror, so it needs 'absolute' + its own `scale` instead.
export function QrPaymentOverlay({ qrPayment, position = 'fixed', scale = 1 }) {
  if (!qrPayment?.qrValue) return null;
  return (
    <Flex position={position} inset={0} zIndex={10000} bg='blackAlpha.700' align='center' justify='center' p={4}>
      <Box
        bg='white'
        borderRadius='20px'
        px='28px'
        py='24px'
        textAlign='center'
        boxShadow='0 20px 50px rgba(0,0,0,0.5)'
        transform={scale !== 1 ? `scale(${scale})` : undefined}
      >
        <Text fontSize='11px' fontWeight='700' letterSpacing='0.16em' color='gray.500' mb={3}>SCAN TO PAY</Text>
        <Flex justify='center' mb={3}>
          <QRCode value={qrPayment.qrValue} size={200} level='M' />
        </Flex>
        {qrPayment.amount != null && (
          <Text fontWeight='800' fontSize='22px' color='gray.800'>
            {qrPayment.amount.toLocaleString('en-US')} VND
          </Text>
        )}
        {qrPayment.expiryAt && (
          <Text fontSize='13px' color='gray.500' mt={1}>
            Expires at {moment(qrPayment.expiryAt).format('DD/MM/YYYY HH:mm:ss')}
          </Text>
        )}
        <Text fontSize='14px' fontWeight='600' color='orange.500' mt={2}>
          Waiting for payment...
        </Text>
      </Box>
    </Flex>
  );
}

// Small chip used in the sticky header (date/time, venue, adult-child count).
function Pill({ children }) {
  return (
    <Box bg='gray.100' borderRadius='full' px={4} py={2} fontSize='lg' fontWeight='medium' whiteSpace='nowrap'>
      {children}
    </Box>
  );
}

// `compact` drops everything but logo + countdown (Confirmation screen
// already shows those details in its ticket card).
// `sticky=false` for CustomerDisplayPreview.js's transform-scaled mirror,
// which would otherwise break position:sticky's containing block.
function StickyHeader({ data, compact = false, dark = false, sticky = true }) {
  return (
    <Flex
      position={sticky ? 'sticky' : 'static'}
      top={sticky ? 0 : undefined}
      zIndex={sticky ? 10 : undefined}
      bg={dark ? TICKET_PAGE_BG : 'white'}
      borderBottom={dark ? 'none' : '1px solid #E2E8F0'}
      align='center'
      wrap='wrap'
      gap={3}
      px={6}
      py={3}
    >
      <Image
        src={logo}
        alt='Lune'
        h='48px'
        objectFit='contain'
        mr={2}
      />
      {!compact && (
        <>
          <Text fontWeight='bold' fontSize='2xl' mr={2}>
            {data.showName}
          </Text>
          <Pill>{data.showDate} · {data.showTime}</Pill>
          <Pill>{data.venueName}</Pill>
          <Pill>{data.adult ?? 0} adults · {data.child ?? 0} children</Pill>
        </>
      )}
      {/* Once a booking code exists the seat hold is moot; the code itself
          is shown by BookingCodeBadge on the ticket card instead. */}
      {!data.bookingCode && <HeaderCountdown expireAt={data.expireAt} dark={dark} />}
    </Flex>
  );
}

function ZoneLegend({ ticketPrices, legendMap }) {
  if (!ticketPrices?.length && !legendMap) return null;
  return (
    <Flex
      w='100%'
      justify='space-between'
      align='flex-start'
      wrap='wrap'
      gap={6}
      mb={8}
    >
      {ticketPrices?.length > 0 && (
        <Box>
          <Text fontWeight='bold' color='#939496' textAlign='center' mb={2}>
            LEGEND IN ZONES
          </Text>
          <Grid
            templateColumns='repeat(3, auto)'
            gap='6px'
            color='white'
            textAlign='center'
          >
            <Text color='#939496'>ZONE</Text>
            <Text color='#939496'>SEATS</Text>
            <Text color='#939496'>OFFICIAL RATE (VND)</Text>
            {ticketPrices.map((i, index) => (
              <React.Fragment key={index}>
                <Text
                  textAlign='center'
                  backgroundColor={colorBackGround(i.zone_id)}
                  px={1}
                  fontWeight='bold'
                  py={2}
                >
                  {i.zone.name}
                </Text>
                <Text
                  textAlign='center'
                  backgroundColor={colorBackGround(i.zone_id)}
                  px={3}
                  fontWeight='bold'
                  py={2}
                >
                  {i?.total_seat}
                </Text>
                <Text
                  textAlign='center'
                  backgroundColor={colorBackGround(i.zone_id)}
                  px={3}
                  fontWeight='bold'
                  py={2}
                >
                  {i.official_price.toLocaleString('en-US')}
                </Text>
              </React.Fragment>
            ))}
          </Grid>
        </Box>
      )}
      {legendMap && <Image maxW='250px' src={legendMap} />}
    </Flex>
  );
}

function SelectionSummaryCard({ seats, totalSeatsNeeded }) {
  const rows = (seats || []).map((seat) => {
    const zoneId = seat.zone?.id ?? seat.zone_id;
    return {
      key: `${seat.seat_number}_${seat.floor}_${zoneId}`,
      seatNumber: seat.seat_number,
      zoneName: seat.zone?.name,
      zoneColor: colorBackGround(zoneId),
      floor: showTextFloor(seat.floor),
    };
  });

  return (
    <Box border='1px solid #E2E8F0' borderRadius='md' p={3} w='100%'>
      <Flex justify='space-between' align='center' mb={2}>
        <Text fontWeight='bold' fontSize='sm'>YOUR SELECTION</Text>
        <Box bg='green.50' color='green.600' borderRadius='full' px={2} py={0.5} fontSize='xs' fontWeight='bold'>
          {rows.length} / {totalSeatsNeeded ?? rows.length} seats
        </Box>
      </Flex>
      {rows.length === 0 ? (
        <Text color='gray.400' fontSize='sm'>No seats selected yet</Text>
      ) : (
        <Box>
          {rows.map((row) => (
            <Flex key={row.key} justify='space-between' align='center' borderBottom='1px solid #E2E8F0' py={2}>
              <Flex align='center' gap={2}>
                <Flex
                  boxSize='44px'
                  borderRadius='md'
                  bg={row.zoneColor}
                  color='white'
                  align='center'
                  justify='center'
                  fontWeight='bold'
                  fontSize='lg'
                  flexShrink={0}
                >
                  {row.seatNumber}
                </Flex>
                <Box>
                  <Text fontWeight='bold' fontSize='xs'>{row.zoneName}</Text>
                  <Text fontSize='xs' color='gray.500'>{row.floor}</Text>
                </Box>
              </Flex>
            </Flex>
          ))}
        </Box>
      )}
    </Box>
  );
}

function SeatingScreen({ data, isPreview = false }) {
  const svgRef = useRef(null);

  useEffect(() => {
    applySeatStyling(
      svgRef.current,
      data?.selectedSeat,
      data?.lockByAdmin,
      data?.seatWaiting,
      data?.seatBooked
    );
  }, [data]);

  if (!data?.desktopMap) {
    return <IdleScreen />;
  }

  const totalSeatsNeeded = (parseInt(data.adult) || 0) + (parseInt(data.child) || 0);

  return (
    <Box minH='100%'>
      <StickyHeader data={data} sticky={!isPreview} />
      <Flex align='flex-start' gap={4} p={6} wrap='wrap'>
        <Box flex='1' minW='0'>
          <Text fontSize='2xl' fontWeight='bold' mb={1}>
            Please select your seats
          </Text>
          <Text color='gray.500' mb={6}>
            {data.selectedSeat?.length || 0} of {totalSeatsNeeded || 0} seats selected
          </Text>
          <ZoneLegend ticketPrices={data.ticketPrices} legendMap={data.legendMap} />
          <Box
            w='100%'
            sx={{ '& svg': { width: '100%', height: 'auto', display: 'block' } }}
          >
            <ReactSVG
              id='svgImageCustomerDisplay'
              src={data.desktopMap}
              afterInjection={(svg) => {
                svgRef.current = svg;
                applySeatStyling(
                  svg,
                  data?.selectedSeat,
                  data?.lockByAdmin,
                  data?.seatWaiting,
                  data?.seatBooked
                );
              }}
            />
          </Box>
        </Box>
        <Box
          w={{ base: '100%', lg: '240px' }}
          flexShrink={0}
          position={isPreview ? 'static' : { base: 'static', lg: 'sticky' }}
          top={isPreview ? undefined : '90px'}
          alignSelf='flex-start'
        >
          <SelectionSummaryCard
            seats={data.seats}
            totalSeatsNeeded={totalSeatsNeeded}
          />
        </Box>
      </Flex>
    </Box>
  );
}

// Mirrors BookingInformation.js's "Change Show Date" modal; read-only here
// (callbacks below are no-ops). Hands back to SeatingScreen on modal close
// (see handleCloseCalendarModal).
function CalendarScreen({ data, isPreview = false }) {
  // This side has no interaction (pointerEvents='none'), so nothing triggers
  // EventCalendar's own navigation. It only calls gotoDate() when
  // `searchKey` increases, so bump it whenever a new month/year arrives.
  const [searchKey, setSearchKey] = useState(0);
  const monthYearKeyRef = useRef(null);

  useEffect(() => {
    if (!data?.calendarData) return;
    const key = `${data.monthUpdate}-${data.yearUpdate}`;
    if (monthYearKeyRef.current === null) {
      // First broadcast already matches `initialDate` at mount — nothing to
      // navigate to yet.
      monthYearKeyRef.current = key;
      return;
    }
    if (monthYearKeyRef.current === key) return;
    monthYearKeyRef.current = key;
    setSearchKey((k) => k + 1);
  }, [data?.calendarData, data?.monthUpdate, data?.yearUpdate]);

  if (!data?.calendarData) {
    return <IdleScreen />;
  }

  return (
    <Box minH='100%'>
      <StickyHeader data={data} sticky={!isPreview} />
      <Box p={6}>
        <Text fontSize='2xl' fontWeight='bold' mb={1}>
          Changing show date
        </Text>
        <Text color='gray.500' mb={6}>
          Current: {data.showName} · {data.showDate} · {data.showTime}
        </Text>
        <Box bg='#ebebeb' p={4} borderRadius='md' pointerEvents='none'>
          <EventCalendar
            calendarData={data.calendarData}
            initialDate={data.initialDate}
            yearUpdate={data.yearUpdate}
            monthUpdate={data.monthUpdate}
            searchKey={searchKey}
            getValueMonthChange={() => {}}
            selectShow={() => {}}
          />
        </Box>
      </Box>
    </Box>
  );
}

// Before a rate plan is picked, every seat is just priced at its zone's
// official rate (no program applied yet, no adult/child split).
function estimatePaymentRows(seats, ticketPrices) {
  return (seats || []).map((seat) => {
    const zoneId = seat.zone?.id ?? seat.zone_id;
    const officialPrice = ticketPrices?.find((tp) => tp.zone_id === zoneId)?.official_price || 0;
    return {
      key: seat.seat_number,
      seatNumber: seat.seat_number,
      officialPrice,
      amount: officialPrice,
      programApplied: null,
    };
  });
}

// Diagonal corner banner (see getBookingOutcome above). The outer Box is a
// clip mask sized to the rotated strip — clipping to the card's own bounds
// would cut into the leading letter.
function OutcomeRibbon({ outcome }) {
  if (!outcome) return null;
  const isSuccess = outcome === 'success';
  return (
    <Box position='absolute' top={0} right={0} w='180px' h='180px' overflow='hidden' zIndex={5}>
      <Box
        position='absolute'
        top='36px'
        right='-68px'
        w='260px'
        py='9px'
        bg={isSuccess ? 'green.500' : 'red.500'}
        color='white'
        textAlign='center'
        fontWeight='800'
        fontSize='15px'
        letterSpacing='0.12em'
        transform='rotate(45deg)'
        boxShadow='0 4px 10px rgba(0,0,0,0.25)'
      >
        {isSuccess ? 'SUCCESS' : 'CANCELLED'}
      </Box>
    </Box>
  );
}

// Purely visual countdown mirroring the real setTimeout in goIdle() — its
// own timer since the two only need to look in sync, not be identical.
function AutoIdleProgressBar({ outcome }) {
  const [elapsedMs, setElapsedMs] = useState(0);

  useEffect(() => {
    const startedAt = Date.now();
    const interval = setInterval(() => {
      setElapsedMs(Date.now() - startedAt);
    }, 100);
    return () => clearInterval(interval);
  }, []);

  const remainingRatio = Math.max(0, 1 - elapsedMs / OUTCOME_AUTO_IDLE_MS);
  const secondsLeft = Math.max(0, Math.ceil((OUTCOME_AUTO_IDLE_MS - elapsedMs) / 1000));
  const isSuccess = outcome === 'success';

  return (
    <Box w='100%' maxW='420px' mt='22px'>
      <Text fontSize='12px' color='whiteAlpha.800' textAlign='center' fontWeight='600' letterSpacing='0.06em' mb='6px'>
        Returning to Welcome in {secondsLeft}s
      </Text>
      <Box bg='whiteAlpha.200' h='5px' borderRadius='full' overflow='hidden'>
        <Box
          h='100%'
          bg={isSuccess ? 'green.400' : 'red.400'}
          w={`${remainingRatio * 100}%`}
          transition='width 0.1s linear'
        />
      </Box>
    </Box>
  );
}

function ConfirmationScreen({ data, isPreview = false }) {
  if (!data) {
    return <IdleScreen />;
  }

  const outcome = getBookingOutcome(data);
  // paymentBreakdown arrives pre-rate-plan too (zone-only pricing); only rows
  // with a real programApplied prove a plan was actually picked (mirrors
  // BookingPayment.js's own submit-gate check).
  const hasRealPricing =
    !!data.paymentBreakdown?.rows?.length &&
    data.paymentBreakdown.rows.every((row) => row.programApplied);
  const paymentRows = hasRealPricing
    ? data.paymentBreakdown.rows.map((row) => ({ ...row, key: row.seatNumber }))
    : estimatePaymentRows(data.seats, data.ticketPrices);
  const total = hasRealPricing
    ? data.paymentBreakdown.total
    : paymentRows.reduce((sum, row) => sum + row.amount, 0);
  const groupedPaymentRows = hasRealPricing ? groupPaymentRows(paymentRows) : [];

  return (
    <Box minH='100%' position='relative'>
      <StickyHeader data={data} compact dark sticky={!isPreview} />
      {/* Absolute over the header (not in flow) so it centers against the
          full screen height, not just the leftover space below the header. */}
      <Flex direction='column' align='center' justify='center' position='absolute' inset={0} p={10}>
        {/* Ticket card: white body on the dark page, dashed "tear line"
            between the two halves, with 2 punch-hole notches (colored to
            match the page bg) biting into the top/bottom of that line. */}
        <Grid
          position='relative'
          w='100%'
          maxW='1440px'
          templateColumns={{ base: '1fr', md: 'minmax(0,1.55fr) minmax(330px,1fr)' }}
          bg='white'
          borderRadius='26px'
          boxShadow='0 30px 60px rgba(0,0,0,0.45)'
        >
          <OutcomeRibbon outcome={outcome} />
          <Box p='30px 34px' display='flex' flexDirection='column'>
            <Flex justify='space-between' align='flex-start' gap={4}>
              <Box>
                <Text fontSize='12px' fontWeight='700' letterSpacing='0.16em' color='gray.500' mb={1}>SHOW</Text>
                <Text fontSize='30px' fontWeight='800' letterSpacing='-0.02em' lineHeight='1.12' mb={1}>{data.showName}</Text>
                <Text fontSize='14px' color='gray.500'>{data.venueName}</Text>
                {data.venueAddress && (
                  <Text fontSize='12px' color='gray.400'>{data.venueAddress}</Text>
                )}
              </Box>
              <Flex align='center' gap={3} flexShrink={0}>
                <BookingCodeBadge data={data} />
                <ZoneBadge data={data} />
              </Flex>
            </Flex>

            <Box borderBottom='1px solid #E2E8F0' my={6} />

            {/* Flex, not an auto-fit grid: grid kept dropping "Floor" to a
                new row once its track didn't fit. */}
            <Flex justify='space-between' gap='18px' mb={6}>
              <InfoItem label='Date' value={data.showDate} />
              <InfoItem label='Time' value={data.showTime} />
              <InfoItem label='Adult' value={data.adult ?? 0} />
              <InfoItem label='Child' value={data.child ?? 0} />
              <InfoItem label='Floor' value={data.floor} />
            </Flex>

            <Box borderBottom='1px solid #E2E8F0' my={6} />

            <Flex align='baseline' gap={2} mb={3} wrap='wrap'>
              <Text fontSize='10px' fontWeight='700' letterSpacing='0.16em' color='gray.500'>
                YOUR SEATS
              </Text>
              <Text fontSize='13px' color='gray.500'>
                · {data.seats?.length ?? 0} seats{data.zone ? ` · ${data.zone}` : ''}
              </Text>
            </Flex>
            <Grid templateColumns='repeat(auto-fill, minmax(100px, 1fr))' gap='10px'>
              {(data.seats || []).map((seat) => {
                const zoneId = seat.zone?.id ?? seat.zone_id;
                const zoneColor = colorBackGround(zoneId) || '#4A5568';
                return (
                  <Box
                    key={`${seat.seat_number}_${seat.floor}_${zoneId}`}
                    border='1px solid'
                    borderColor={zoneColor}
                    bg={`${zoneColor}1A`}
                    borderRadius='12px'
                    pt='12px'
                    px='8px'
                    pb='10px'
                    textAlign='center'
                  >
                    <Text fontSize='20px' fontWeight='800' letterSpacing='-0.01em' lineHeight='1.1' color='gray.800'>
                      {seat.seat_number}
                    </Text>
                  </Box>
                );
              })}
            </Grid>

            {(data.booker?.name || data.audience?.name) && (
              <Box mt='auto' pt={6}>
                <Box borderBottom='1px solid #E2E8F0' mb={6} />
                <Grid templateColumns='repeat(2, 1fr)' gap='14px'>
                  <PartyCard label='BOOKER' party={data.booker} />
                  <PartyCard label='AUDIENCE' party={data.audience} />
                </Grid>
              </Box>
            )}
          </Box>

          <Box
            position='relative'
            p='30px 28px'
            borderLeft={{ base: 'none', md: '2px dashed #CBD5E0' }}
            borderTopRightRadius='26px'
            borderBottomRightRadius='26px'
            bg='gray.50'
          >
            {/* Punch-hole notches on the tear line — translate(-50%) from a
                0,0 corner keeps them exactly on the seam regardless of
                box-sizing. */}
            <Box
              display={{ base: 'none', md: 'block' }}
              position='absolute'
              top='0'
              left='0'
              transform='translate(-50%, -50%)'
              boxSize='28px'
              borderRadius='50%'
              bg={TICKET_PAGE_BG}
              zIndex={2}
            />
            <Box
              display={{ base: 'none', md: 'block' }}
              position='absolute'
              bottom='0'
              left='0'
              transform='translate(-50%, 50%)'
              boxSize='28px'
              borderRadius='50%'
              bg={TICKET_PAGE_BG}
              zIndex={2}
            />

            <Text fontSize='12px' fontWeight='700' letterSpacing='0.16em' color='gray.500' mb={3}>PAYMENT SUMMARY</Text>
            {hasRealPricing && groupedPaymentRows.map((group, index) => {
              const groupTotal = group.unitAmount * group.seatNumbers.length;
              return (
                <Box
                  key={group.key}
                  py='13px'
                  borderBottom={index !== groupedPaymentRows.length - 1 ? '1px solid #E2E8F0' : 'none'}
                >
                  <Flex justify='space-between' align='baseline' mb={2}>
                    <Text fontWeight='700' fontSize='17px'>
                      {group.unitAmount.toLocaleString('en-US')} × {group.seatNumbers.length}
                    </Text>
                    <Text fontWeight='800' fontSize='17px'>
                      {groupTotal.toLocaleString('en-US')}
                    </Text>
                  </Flex>
                  <Flex wrap='wrap' gap={1.5}>
                    {group.seatNumbers.map((seatNumber) => (
                      <Box key={seatNumber} bg='white' color='gray.600' borderRadius='6px' px='8px' py='2px' fontSize='13px' fontWeight='700'>
                        {seatNumber}
                      </Box>
                    ))}
                  </Flex>
                </Box>
              );
            })}
            {hasRealPricing && !data.balance && (
              <Box borderTop='1px solid #E2E8F0' mt={2} pt={2}>
                <Flex justify='space-between' align='center'>
                  <Text fontWeight='bold' fontSize='sm'>TOTAL</Text>
                  <Text fontWeight='bold' fontSize='xl' color='green.500'>
                    {total.toLocaleString('en-US')} VND
                  </Text>
                </Flex>
              </Box>
            )}

            <BalanceCard balance={data.balance} />
          </Box>
        </Grid>
        {outcome && <AutoIdleProgressBar outcome={outcome} />}
      </Flex>
    </Box>
  );
}
